import type { TourStepDefinition } from '@dev_mat/floating-ui-tour';
import { shift, size, type Middleware } from '@floating-ui/dom';

const VIEWPORT_ANCHOR_ID = 'tour-viewport-anchor';

const VIEWPORT_PADDING = 12;

/**
 * Middleware that keeps the popover inside the viewport even when the target
 * is taller (or wider) than the viewport. The library's built-in shift only
 * handles the cross axis, so a popover anchored to a full-height list ends up
 * overflowing off-screen. We add a main-axis shift plus a size constraint so
 * the popover always renders somewhere visible, scrolling internally if its
 * own content would otherwise exceed the viewport.
 */
const VIEWPORT_FIT_MIDDLEWARE: Middleware[] = [
  shift({ mainAxis: true, crossAxis: true, padding: VIEWPORT_PADDING }),
  size({
    padding: VIEWPORT_PADDING,
    apply({ availableHeight, elements }) {
      Object.assign(elements.floating.style, {
        maxHeight: `${Math.max(180, Math.floor(availableHeight))}px`,
        overflowY: 'auto',
      });
    },
  }),
];

/**
 * Lazily mounts a 1px hidden anchor in the upper-middle of the viewport. Used
 * by `introStep` so "welcome to this page" popovers render as a centered
 * modal-like card instead of being anchored to a viewport-sized wrapper
 * (where floating-ui has nowhere to place them and they overflow off-screen).
 */
function ensureViewportAnchor(): HTMLElement {
  let el = document.getElementById(VIEWPORT_ANCHOR_ID) as HTMLElement | null;
  if (!el) {
    el = document.createElement('div');
    el.id = VIEWPORT_ANCHOR_ID;
    el.setAttribute('aria-hidden', 'true');
    Object.assign(el.style, {
      position: 'fixed',
      top: '40%',
      left: '50%',
      width: '1px',
      height: '1px',
      pointerEvents: 'none',
      opacity: '0',
    });
    document.body.appendChild(el);
  }
  return el;
}

/**
 * Returns true only if the element is in the DOM and visually rendered.
 * Catches `display: none`, `visibility: hidden`, and zero-sized elements.
 */
export function isElementVisible(target: string | HTMLElement): boolean {
  const el =
    typeof target === 'string' ? document.querySelector(target) : target;
  if (!(el instanceof HTMLElement)) return false;
  if (!el.isConnected) return false;

  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;

  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden') return false;

  return true;
}

/**
 * Sugar for steps whose target may be conditionally rendered (permission gate,
 * loading state, empty state). The library auto-advances when a target is
 * missing — this catches the in-DOM-but-hidden case too.
 */
export function skipIfHidden(): TourStepDefinition['onBeforeShow'] {
  return (step) => isElementVisible(step.target);
}

/**
 * Wrap any step to gain hidden-target skipping plus viewport-aware positioning.
 * The added middleware keeps the popover on screen even when the anchored
 * target is larger than the viewport (a common case for full-page tables/lists).
 *
 *   step({ target: '#foo', title: 'Foo', content: '…' })
 */
export function step(def: TourStepDefinition): TourStepDefinition {
  const userBeforeShow = def.onBeforeShow;
  const userMiddleware = def.middleware ?? [];
  return {
    ...def,
    middleware: [...VIEWPORT_FIT_MIDDLEWARE, ...userMiddleware],
    onBeforeShow: (s, i, tour) => {
      if (!isElementVisible(s.target)) return false;
      return userBeforeShow ? userBeforeShow(s, i, tour) : undefined;
    },
  };
}

export type IntroStepInput = Omit<
  TourStepDefinition,
  'target' | 'placement' | 'offsetValue'
> & {
  placement?: 'top' | 'bottom';
};

/**
 * "Welcome to this page" step that renders the popover at viewport center
 * instead of anchoring to a page element. Use this for the first step of a
 * tour that introduces the whole page — anchoring to a full-viewport wrapper
 * leaves floating-ui with nowhere to place the popover.
 *
 *   introStep({ title: 'Welcome', content: '…' })
 */
export function introStep(def: IntroStepInput): TourStepDefinition {
  return {
    ...def,
    content: def.content,
    target: ensureViewportAnchor(),
    placement: def.placement ?? 'bottom',
    offsetValue: 0,
  };
}
